import { ChangeEvent, FC, ReactNode, useRef, useState } from 'react';
import { BsCheck2Circle } from 'react-icons/bs';
import { CgClose, CgMathPlus } from 'react-icons/cg';
import { IconType } from 'react-icons/lib';
import { v4 as uuidv4 } from 'uuid';
import {
  Box,
  BoxProps,
  Button,
  Flex,
  Icon,
  Spinner,
  Stack,
  Text,
  useRecipe,
} from '@chakra-ui/react';

import { DocStatusEnum } from '../../constants';
import { Nullable } from '../../types';
import {
  FormFieldControlledComponent,
  FormFieldControlledComponentProps,
} from '../../types/fields';

// @TODO Need to remove thorn dependency here

export type FileDisplayResource = {
  filename: string;
  id: string;
  status?: string;
  rejectionReason?: string;
};

type FileUploadProps<M = Record<string, unknown>> = {
  containerProps?: BoxProps;
  fileUploadTileProps?: BoxProps;
  disabled?: boolean;
  metaData?: M;
  multiple?: boolean;
  onRemoveCallback?: (file: FileDisplayResource) => Promise<void>;
  onUploadCallback?: (file: File, metaData?: M) => Promise<FileDisplayResource>;
  slot?: ReactNode;
  showAddFileButton?: boolean;
};

type FileUploadTileProps = {
  status?: string;
  rejectionReason?: string;
  label: string;
  onRemove?: () => void;
  state: 'uploaded' | 'uploading';
};
const isRejected = (status: string | undefined): boolean =>
  status === DocStatusEnum.Rejected;

const getIcon = (status: string | undefined): IconType =>
  isRejected(status) ? CgClose : BsCheck2Circle;

const getIconColor = (status: string | undefined): string =>
  isRejected(status) ? 'red' : 'primary.500';

const FileUploadTile: FC<FileUploadTileProps & BoxProps> = ({
  status,
  rejectionReason,
  label,
  onRemove,
  state,
  ...fileUploadTileProps
}) => {
  const recipe = useRecipe({ key: 'fileUploadTile' });
  const fileUploadTileStyle = recipe();

  return (
    <Box
      bg="secondary.700"
      borderRadius="sm"
      css={fileUploadTileStyle}
      {...fileUploadTileProps}
      justifyContent="space-between"
      px={4}
      py={6}
    >
      <Flex justifyContent="space-between">
        <Flex alignItems="center" maxW="calc(100% - 48px)">
          {state === 'uploaded' ? (
            <Icon
              as={getIcon(status)}
              boxSize={6}
              color={getIconColor(status)}
            />
          ) : (
            <Spinner
              borderWidth="2px"
              boxSize={5}
              css={{ '--spinner-track-color': 'colors.secondary.750' }}
            />
          )}

          <Text lineClamp={1} ml={2} wordBreak="break-all">
            {label}
          </Text>
        </Flex>
        {onRemove && state === 'uploaded' && (
          <Box as="button" ml={4} onClick={onRemove}>
            <Icon as={CgClose} boxSize={6} color="primary.500" />
          </Box>
        )}
      </Flex>
      {status && isRejected(status) && (
        <Flex>
          <Box>
            <Text>Document feedback</Text>
            <Text
              background="#dddddd"
              border="1px solid #4d4a4a"
              borderRadius="4px"
              color="red"
              padding="10px"
            >
              {rejectionReason}
            </Text>
          </Box>
        </Flex>
      )}
    </Box>
  );
};

export const FileUpload: FormFieldControlledComponent<
  FileDisplayResource[],
  FileUploadProps
> = ({
  _ref,
  containerProps,
  fileUploadTileProps,
  disabled,
  invalid,
  metaData,
  multiple,
  onChange,
  onRemoveCallback,
  onUploadCallback,
  slot,
  showAddFileButton,
  value = [],
  ...props
}: FormFieldControlledComponentProps<
  FileDisplayResource[],
  FileUploadProps
>) => {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [uploadingFiles, setUploadingFiles] = useState<FileDisplayResource[]>(
    [],
  );
  const inputRef = useRef<Nullable<HTMLInputElement>>(null);

  const onChooseFile = (): void => inputRef.current?.click();

  const onRemove = async (file: FileDisplayResource): Promise<void> => {
    if (onRemoveCallback) {
      await onRemoveCallback(file);
    }

    onChange(value.filter((f) => f.id !== file.id));
  };

  const onUpload = async (
    event: ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const targetFiles = event.target.files;

    if (targetFiles && targetFiles.length > 0) {
      setErrorMessage('');

      const files = Array.from({ length: targetFiles.length })
        .map((_, idx) => targetFiles.item(idx))
        .filter((e) => !!e) as File[];

      const fileResources: FileDisplayResource[] = files.map((file) => ({
        filename: file.name,
        id: uuidv4(),
      }));

      if (onUploadCallback) {
        setUploadingFiles((prevUploadingFiles) => [
          ...prevUploadingFiles,
          ...fileResources,
        ]);

        files.forEach((file, idx) => {
          const fileResource = fileResources[idx];

          onUploadCallback(file, metaData)
            .then((uploadedFileResource) => {
              onChange([...value, uploadedFileResource]);
            })
            .catch((error) => {
              let uploadErrorMessage = `Failed to upload ${file.name}. Please try another file.`;
              if (error.name === 'ValidationError') {
                uploadErrorMessage = `Failed to upload ${file.name}. Please try another file. ${error.message}`;
              }
              setErrorMessage(uploadErrorMessage);
            })
            .finally(() => {
              setUploadingFiles((prevUploadingFiles) =>
                prevUploadingFiles.filter((f) => f.id !== fileResource.id),
              );
            });
        });
      } else {
        onChange([...value, ...fileResources]);
      }

      // Reset target value so that the user can upload the previously removed file
      inputRef.current!.value = '';
    }
  };

  return (
    <Box {...containerProps}>
      <Box asChild srOnly>
        <input
          ref={inputRef}
          disabled={disabled}
          id={props.name}
          multiple={multiple}
          onChange={onUpload}
          type="file"
          {...props}
        />
      </Box>

      <Stack gap={6}>
        {(value.length > 0 || uploadingFiles.length > 0) && (
          <Stack gap={1}>
            {value.map((file) => (
              <FileUploadTile
                key={file.id}
                label={file.filename}
                onRemove={() => onRemove(file)}
                rejectionReason={file.rejectionReason}
                state="uploaded"
                status={file.status}
                {...fileUploadTileProps}
              />
            ))}

            {uploadingFiles.map((file) => (
              <FileUploadTile
                key={file.id}
                label={file.filename}
                rejectionReason={file.rejectionReason}
                state="uploading"
                status={file.status}
                {...fileUploadTileProps}
              />
            ))}
          </Stack>
        )}

        {slot}

        {(value.length === 0 || multiple || showAddFileButton) && (
          <Button disabled={disabled} onClick={onChooseFile}>
            <CgMathPlus />
            Add {multiple && value.length > 0 && 'another'} file
          </Button>
        )}
      </Stack>

      {errorMessage && (
        <Text color="red.500" mt={4}>
          {errorMessage}
        </Text>
      )}
    </Box>
  );
};

FileUpload.controlled = true;
FileUpload.displayName = 'FileUpload';
